/** Puerto SheetsApi sobre la API REST de Google Sheets (escrituras del dispositivo con el token del usuario). */
import type { SheetsApi } from '@sut/workbook';

export function sheetsApi(fileId: string, token: () => Promise<string>): SheetsApi {
  const base = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(fileId)}`;
  const call = async <T>(url: string, init: RequestInit = {}): Promise<T> => {
    const r = await fetch(url, { ...init, headers: { authorization: `Bearer ${await token()}`, 'content-type': 'application/json', ...(init.headers ?? {}) } });
    if (!r.ok) {
      const j = await r.json().catch(() => ({})) as { error?: { message?: string } };
      throw Object.assign(new Error(`Google: ${j.error?.message ?? r.status}`), { status: r.status });
    }
    return r.json() as Promise<T>;
  };
  return {
    async get(range) {
      const r = await call<{ values?: unknown[][] }>(`${base}/values/${encodeURIComponent(range)}?valueRenderOption=UNFORMATTED_VALUE&dateTimeRenderOption=SERIAL_NUMBER`);
      return r.values ?? [];
    },
    async update(data, input = 'RAW') {
      await call(`${base}/values:batchUpdate`, { method: 'POST', body: JSON.stringify({ valueInputOption: input, data }) });
    },
    async append(range, values, input = 'RAW') {
      const r = await call<{ updates: { updatedRange: string } }>(`${base}/values/${encodeURIComponent(range)}:append?valueInputOption=${input}&insertDataOption=INSERT_ROWS`,
        { method: 'POST', body: JSON.stringify({ values }) });
      return { updatedRange: r.updates.updatedRange };
    },
    async clear(ranges) {
      await call(`${base}/values:batchClear`, { method: 'POST', body: JSON.stringify({ ranges }) });
    },
    async addSheet(title) {
      await call(`${base}:batchUpdate`, { method: 'POST', body: JSON.stringify({ requests: [{ addSheet: { properties: { title } } }] }) });
    },
    async deleteRows(rows) {
      if (!rows.length) return;
      // De abajo arriba, para que los números de fila no se muevan entre una y otra
      const meta = await call<{ sheets: Array<{ properties: { sheetId: number; title: string } }> }>(`${base}?fields=sheets.properties(sheetId,title)`);
      const idOf = new Map(meta.sheets.map(x => [x.properties.title, x.properties.sheetId]));
      const requests = [...rows].sort((a, b) => b.row - a.row).map(x => {
        const sheetId = idOf.get(x.tab);
        if (sheetId === undefined) throw new Error(`No existe la pestaña ${x.tab}`);
        return { deleteDimension: { range: { sheetId, dimension: 'ROWS', startIndex: x.row - 1, endIndex: x.row } } };
      });
      await call(`${base}:batchUpdate`, { method: 'POST', body: JSON.stringify({ requests }) });
    },
  };
}
